import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Upload, ExternalLink, Ban, Link2, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { mandateAPI, leadsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/* SEPA-Mandate (v7 M12): ein Objekt je Mandat — aus dem Verkaufsrechner
   (Unterschrift am Tablet) oder aus den Sticky-Dateien des Bestands.
   Kein Klartext-IBAN in der Liste; Zuordnung offener Import-Mandate von Hand. */
const P1_ROT = '#E60000';
const STATUS_LABEL = { erteilt: 'erteilt', aktiv: 'aktiv', widerrufen: 'widerrufen', verfallen: 'verfallen' };
const STATUS_CLS = { erteilt: 'bg-sky-500/15 text-sky-700', aktiv: 'bg-emerald-500/15 text-emerald-700', widerrufen: 'bg-muted text-muted-foreground', verfallen: 'bg-amber-500/15 text-amber-700' };
const ZUORD_LABEL = { sicher: 'zugeordnet', vorschlag: 'Vorschlag prüfen', offen: 'ohne Zuordnung' };
const HERKUNFT_LABEL = { verkaufsrechner: 'Tablet', sticky_import: 'Sticky', manuell: 'manuell', papier_foto: 'Papierfoto', zahlerwechsel: 'Zahlerwechsel' };

function datum(iso) { if (!iso) return '–'; const s = String(iso); return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`; }

function ZuordnenZeile({ m, onFertig }) {
  const [suche, setSuche] = useState(m.zahler_name || '');
  const [treffer, setTreffer] = useState([]);
  const [laden, setLaden] = useState(false);
  useEffect(() => {
    if (suche.trim().length < 2) { setTreffer([]); return; }
    const t = setTimeout(async () => {
      setLaden(true);
      try { const r = await leadsAPI.getAll({ search: suche.trim(), limit: 8 }); setTreffer(r.data.leads || []); } catch { setTreffer([]); }
      setLaden(false);
    }, 300);
    return () => clearTimeout(t);
  }, [suche]);
  const waehlen = async (lead) => {
    try { await mandateAPI.zuordnen(m.id, lead.id); toast.success(`Zugeordnet: ${lead.name}`); onFertig(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Zuordnen fehlgeschlagen'); }
  };
  return (
    <div className="mt-2 rounded-md border border-dashed border-border bg-muted/30 p-3 space-y-2" data-testid={`zuordnen-${m.id}`}>
      {m.kandidaten?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="text-muted-foreground">Vorschläge:</span>
          {m.kandidaten.map(k => <button key={k.id} className="rounded-full border border-border px-2 py-0.5 hover:border-primary" onClick={() => waehlen({ id: k.id, name: k.name })}>{k.name} · {Math.round(k.konfidenz * 100)} %</button>)}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
        <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Mitglied oder Lead suchen …" className="pl-8 h-8 text-sm" />
      </div>
      {laden && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      {treffer.length > 0 && <ul className="text-sm divide-y divide-border/50">{treffer.map(l => <li key={l.id} className="py-1 flex items-center justify-between gap-2"><span>{l.name} <span className="text-xs text-muted-foreground">{l.phone || l.email || ''}</span></span><Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => waehlen(l)}>Zuordnen</Button></li>)}</ul>}
    </div>
  );
}

export default function MandatePage() {
  const navigate = useNavigate();
  const { user, isAdminPermissions } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;
  const [eintraege, setEintraege] = useState([]);
  const [stat, setStat] = useState(null);
  const [laden, setLaden] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterZuord, setFilterZuord] = useState('');
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(null);
  const datei = useRef(null);
  const [importiert, setImportiert] = useState(false);

  const lade = useCallback(async () => {
    setLaden(true);
    const [l, s] = await Promise.allSettled([mandateAPI.liste({ status: filterStatus || undefined, zuordnung: filterZuord || undefined, suche: suche || undefined }), mandateAPI.statistik()]);
    if (l.status === 'fulfilled') setEintraege(l.value.data.eintraege || []); else toast.error('Mandate konnten nicht geladen werden');
    if (s.status === 'fulfilled') setStat(s.value.data);
    setLaden(false);
  }, [filterStatus, filterZuord, suche]);
  useEffect(() => { const t = setTimeout(lade, suche ? 300 : 0); return () => clearTimeout(t); }, [lade, suche]);

  const hochladen = async (files) => {
    if (!files?.length) return;
    setImportiert(true);
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('dateien', f));
      const r = await mandateAPI.importSticky(fd);
      const d = r.data;
      toast.success(`${d.neu} neu, ${d.aktualisiert} aktualisiert (${d.sicher} zugeordnet, ${d.vorschlag} Vorschläge, ${d.offen} offen)`);
      if (d.fehler?.length) toast.warning(`${d.fehler.length} übersprungen — erste: ${d.fehler[0]}`);
      lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import fehlgeschlagen'); }
    setImportiert(false);
    if (datei.current) datei.current.value = '';
  };

  const widerrufen = async (m) => {
    const grund = window.prompt(`Mandat ${m.mandatsreferenz} widerrufen — Grund?`, 'Kontowechsel');
    if (grund === null) return;
    try { await mandateAPI.widerrufen(m.id, grund); toast.success('Widerrufen'); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Widerruf fehlgeschlagen'); }
  };

  const kacheln = useMemo(() => stat ? [
    ['Gesamt', stat.gesamt], ['Aktiv', stat.status?.aktiv || 0], ['Erteilt (noch kein Einzug)', stat.status?.erteilt || 0],
    ['Vorschlag prüfen', stat.zuordnung?.vorschlag || 0], ['Ohne Zuordnung', stat.zuordnung?.offen || 0],
  ] : [], [stat]);

  return (
    <div className="space-y-5" data-testid="mandate-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">SEPA-Mandate</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Ein Mandat je Zahler — vom Tablet oder aus dem Sticky-Bestand. IBAN nur maskiert.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {leitung && (
            <>
              <input ref={datei} type="file" accept=".xml" multiple className="hidden" onChange={e => hochladen(e.target.files)} data-testid="sticky-datei" />
              <Button size="sm" variant="outline" className="gap-1.5" disabled={importiert} onClick={() => datei.current?.click()} title="Sticky pain.008-Dateien (SEPA_*.xml) hochladen — Mandate werden übernommen oder aktualisiert">
                {importiert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Sticky-Dateien importieren
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={lade} disabled={laden} className="gap-1.5">{laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aktualisieren</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {kacheln.map(([l, w]) => (
          <Card key={l} className="border-border/60"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="text-xl font-bold tabular-nums">{w}</p></CardContent></Card>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name oder Mandatsreferenz" className="pl-8 h-9" data-testid="mandate-suche" />
        </div>
        {[['', 'Alle'], ['aktiv', 'Aktiv'], ['erteilt', 'Erteilt'], ['widerrufen', 'Widerrufen'], ['verfallen', 'Verfallen']].map(([v, l]) => (
          <button key={v} onClick={() => setFilterStatus(v)} className={`rounded-full px-3 h-8 text-xs border ${filterStatus === v ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/40'}`}>{l}</button>
        ))}
        <span className="text-border">|</span>
        {[['', 'Alle Zuordnungen'], ['vorschlag', 'Vorschlag prüfen'], ['offen', 'Ohne Zuordnung']].map(([v, l]) => (
          <button key={v} onClick={() => setFilterZuord(v)} className={`rounded-full px-3 h-8 text-xs border ${filterZuord === v ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground/40'}`}>{l}</button>
        ))}
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0 overflow-x-auto">
          {eintraege.length === 0 && !laden && <p className="p-4 text-sm text-muted-foreground">Keine Mandate{suche || filterStatus || filterZuord ? ' für diesen Filter' : ''}. {leitung ? 'Sticky-Dateien (SEPA_*.xml) oben importieren.' : ''}</p>}
          {eintraege.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                <th className="text-left px-3 py-2">Zahler</th><th className="text-left px-3 py-2">Referenz</th><th className="text-left px-3 py-2">IBAN</th>
                <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Sequenz</th><th className="text-left px-3 py-2">Erteilt</th>
                <th className="text-left px-3 py-2">Letzter Einzug</th><th className="text-left px-3 py-2">Herkunft</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {eintraege.map(m => (
                  <>
                    <tr key={m.id} className="border-b border-border/40 hover:bg-muted/30" data-testid={`mandat-${m.mandatsreferenz}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {m.zahler_id ? <button className="hover:underline inline-flex items-center gap-0.5" style={{ color: P1_ROT }} onClick={() => navigate(`/leads/${m.zahler_id}`)}>{m.zahler_name || m.kontoinhaber} <ExternalLink className="w-3 h-3" /></button> : <span>{m.kontoinhaber || m.zahler_name}</span>}
                          {m.zuordnung !== 'sicher' && <Badge variant={m.zuordnung === 'vorschlag' ? 'destructive' : 'secondary'} className="text-[10px]">{ZUORD_LABEL[m.zuordnung]}</Badge>}
                        </div>
                        {m.kontoinhaber && m.zahler_name && m.kontoinhaber !== m.zahler_name && <p className="text-xs text-muted-foreground">Konto: {m.kontoinhaber}</p>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{m.mandatsreferenz}</td>
                      <td className="px-3 py-2 font-mono text-xs">{m.iban_maskiert}</td>
                      <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_CLS[m.status] || ''}`}>{STATUS_LABEL[m.status] || m.status}</span></td>
                      <td className="px-3 py-2 text-xs">{m.sequenz_naechste}{m.status === 'erteilt' && m.erster_einzug_ab ? <span className="block text-muted-foreground">ab {datum(m.erster_einzug_ab)}</span> : null}</td>
                      <td className="px-3 py-2 text-xs">{datum(m.erteilt_am)}</td>
                      <td className="px-3 py-2 text-xs">{datum(m.letzte_nutzung_am)}{m.nachweis?.anzahl_einzuege ? <span className="block text-muted-foreground">{m.nachweis.anzahl_einzuege} Einzüge</span> : null}</td>
                      <td className="px-3 py-2 text-xs">{HERKUNFT_LABEL[m.herkunft] || m.herkunft}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {leitung && m.zuordnung !== 'sicher' && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => setOffen(o => o === m.id ? null : m.id)}><Link2 className="w-3 h-3" /> Zuordnen</Button>}
                        {leitung && (m.status === 'aktiv' || m.status === 'erteilt') && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground" onClick={() => widerrufen(m)}><Ban className="w-3 h-3" /> Widerrufen</Button>}
                      </td>
                    </tr>
                    {offen === m.id && <tr key={m.id + '-z'}><td colSpan={9} className="px-3 pb-3"><ZuordnenZeile m={m} onFertig={() => { setOffen(null); lade(); }} /></td></tr>}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <p className="text-[11px] text-muted-foreground">Sticky-Referenzen bleiben unverändert, damit die Bank keinen neuen Erstlauf braucht. Neue Mandate vom Tablet heißen P1-Jahr-Nummer. Erster Einzug 14 Tage nach Erteilung, bei Mandaten von der Unterschrift-Station mit verkürzter Vorabankündigung am Folgetag; 36 Monate ohne Einzug verfällt ein Mandat.</p>
    </div>
  );
}
